import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {AuthService} from "@auth0/auth0-angular";
import {BookingData, BookingState, BookingStore} from "./booking.store";
import {
  catchError,
  defer,
  EMPTY, expand,
  filter,
  finalize,
  first,
  last,
  map,
  Observable,
  switchMap,
  tap,
  timeout
} from "rxjs";
import {Booking} from "../../data-access/api-booking/Booking";
import {arrayRemove, arrayUpsert} from "@datorama/akita";
import {retryBackoff} from "../../data-access/retry.service";
import {PageResponse} from "../../data-access/PageResponse";


@Injectable({ providedIn: 'root' })
export class BookingStoreService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  constructor(
    private store: BookingStore,
    private httpClient: HttpClient,
    private authService: AuthService
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.setLoading(true);

    this.authService.isLoading$
      .pipe(
        // wait until SDK finished init/callback
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        // safe: token is ready, interceptor will attach it
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          return this.loadPriorityThenShadow$();
        }),
        finalize(() => this.setLoading(false)),
        catchError((err) => {
          console.error('[bootstrap bookings] init failed', err);
          return EMPTY;
        })
      ).subscribe();
  }

  private loadPriorityThenShadow$(): Observable<void> {
    return this.loadPriority$(500).pipe(
      tap(() => {
        // this.loadShadowAll$(100).subscribe();
      }),
      map(() => void 0)
    );
  }

  private loadPriority$(size: number): Observable<any> {
    return this.getPaginateBookings({ page: 0, size, sort: 'dateUpd,desc' })
      .pipe(
        timeout({ first: 12000 }),
        // retry léger sur call critique
        retryBackoff(1, 500),
        tap((res: PageResponse<Booking>) => {
          this.store.update((state: BookingState) => ({
            ...state,
            bookingPage: 0,
            bookings: this.mergeBookings(state.bookings, res.items),
            bookingTotalItems: res.totalItems,
            error: undefined
          }));
          console.log('[bootstrap bookings] all datasets loaded');
        }),
        catchError((error) => {
          this.store.setError(error);
          return EMPTY;
        })
      );
  }

  public loadShadowNextPage$(size: number): Observable<PageResponse<Booking>> {
    const state: BookingData = this.store.getValue();
    if (!state.bookingHasNext) {
      return EMPTY;
    }

    const page: number = state.bookingPage + 1;

    return this.getPaginateBookings({ page, size, sort: 'dateUpd,desc' })
      .pipe(
        timeout({ first: 8000 }),
        retryBackoff(2, 400),
        tap((res: PageResponse<Booking>) => {
          this.store.update((state: BookingState) => ({
            bookings: this.mergeBookings(state.bookings, res.items),
            bookingPage: page,
            bookingHasNext: res.hasNext,
            bookingTotalItems: res.totalItems,
            error: undefined
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return EMPTY;
        })
      );
  }

  private loadShadowAll$(size: number): Observable<void> {
    return defer(() => this.loadShadowNextPage$(size)).pipe(
      expand(() => {
        const state = this.store.getValue();
        return state.bookingHasNext ? this.loadShadowNextPage$(size) : EMPTY;
      }),
      last(undefined, undefined),
      map(() => void 0)
    );
  }

  private getPaginateBookings(params: { page?: number; size?: number; sort?: string; }): Observable<PageResponse<Booking>> {
    const url: string = `${this.baseRef}${this.serviceCore}${this.apiV1}bookings/pageable`;

    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 50))
      .set('sort', params.sort ?? 'dateUpd,desc');
    return this.httpClient.get<PageResponse<Booking>>(url, { params: httpParams });
  }

  private mergeBookings(existing: Booking[], incoming: Booking[]): Booking[] {
    const map = new Map<string, Booking>();
    for (const b of existing) map.set(b.id, b);
    for (const b of incoming) map.set(b.id, b);

    return Array.from(map.values());
  }

  /**
   *
   * UPDATE & DELETE
   *
   */

  public upsertBooking(booking: Booking): void {
    this.store.update((state: BookingState) => {
      const bookings: Booking[] = arrayUpsert(state.bookings, booking.id, booking);
      return {
        ...state,
        bookings
      };
    });
  }

  public async deleteBooking(bookingId: string): Promise<boolean> {
    return new Promise<boolean>((resolve => {
      this.store.update((state: BookingState) => {
        const bookings: Booking[] = arrayRemove(state.bookings, bookingId);
        return {
          ...state,
          bookings
        };
      });
      resolve(true);
    }));
  }

}
